import { useCallback, useEffect, useState } from "react";

const VIEWPORT = "[data-radix-scroll-area-viewport]";

/**
 * True while a Radix `ScrollArea` has content below its fold (1px tolerance). Pass `ref` to the
 * `ScrollArea` root; the hook finds the Viewport inside it, listens to `scroll`, and re-measures
 * when the viewport or its content resizes (a `ResizeObserver`, skipped where it is absent).
 */
export function useContentBelowFold(): { ref: (node: HTMLElement | null) => void; below: boolean } {
  const [root, setRoot] = useState<HTMLElement | null>(null);
  const [below, setBelow] = useState(false);
  const ref = useCallback((node: HTMLElement | null) => setRoot(node), []);

  useEffect(() => {
    const viewport = root?.querySelector<HTMLElement>(VIEWPORT);
    if (!viewport) {
      setBelow(false);
      return;
    }
    const measure = () =>
      setBelow(viewport.scrollTop + viewport.clientHeight < viewport.scrollHeight - 1);
    measure();
    viewport.addEventListener("scroll", measure);
    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(measure);
      observer.observe(viewport);
      Array.from(viewport.children).forEach((child) => observer?.observe(child));
    }
    return () => {
      viewport.removeEventListener("scroll", measure);
      observer?.disconnect();
    };
  }, [root]);

  return { ref, below };
}
